import { Endpoints } from '@/lib/Endpoints';
import { Requester } from '@/lib/Requester';
import type { GenericResponse } from '@/models/GenericResponse';
import type { Product } from '@/models/Product';
import type { SlugResponse } from '@/models/Slug';

export type CatalogListKind = 'slug' | 'all-products';

export type CatalogListPage = {
  items: Product[];
  total: number;
  nextCursor: number | null;
};

const MAX_PAGE_SIZE = 60;

type AllProductsPayload = {
  products?: Product[];
  totalCount?: number;
};

export async function fetchCatalogListPage(opts: {
  kind: CatalogListKind;
  slug: string;
  sort?: string;
  filters?: string;
  locationSlug?: string;
  limit: number;
  offset: number;
}): Promise<CatalogListPage> {
  const limit = Math.min(Math.max(opts.limit || 24, 1), MAX_PAGE_SIZE);
  const offset = Math.max(opts.offset || 0, 0);
  const page = Math.floor(offset / limit) + 1;

  if (opts.kind === 'all-products') {
    const params: Record<string, string | number> = {
      page,
      rowsPerPage: limit,
    };
    if (opts.sort) params.sort = Number(opts.sort);
    if (opts.filters) params.filter = opts.filters;

    const { data: result } = await Requester.get<GenericResponse<AllProductsPayload>>(
      Endpoints.GET_ALL_PRODUCTS,
      { params },
    );
    const list = (result?.data?.products ?? []) as Product[];
    const total = result?.data?.totalCount ?? list.length;
    const nextCursor = offset + limit < total ? offset + limit : null;
    return { items: list, total, nextCursor };
  }

  const params: Record<string, string | number> = {
    slug: opts.slug,
    page,
    rowsPerPage: limit,
  };
  if (opts.sort) params.sort = Number(opts.sort);
  if (opts.filters) params.filter = opts.filters;
  if (opts.locationSlug) params.locationSlug = opts.locationSlug;

  const { data: result } = await Requester.get<GenericResponse<SlugResponse>>(Endpoints.GET_SLUG, {
    params,
  });
  const list = (result?.data?.category?.products ?? []) as Product[];
  const total = result?.data?.category?.totalCount ?? list.length;
  const nextCursor = offset + limit < total ? offset + limit : null;
  return { items: list, total, nextCursor };
}
